import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/about';

const FORM_URL = 'https://readdy.ai/api/form/d9rhnp7nahb7382ovmq0';

export default function About() {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    document.title = '关于 luluca - 品牌介绍与联系方式 | luluca官网';
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormSubmitted(true);
      setFormState({ name: '', email: '', message: '' });
      return;
    }

    if (!formState.name.trim() || !formState.email.trim() || !formState.message.trim()) {
      setFormError('请填写所有必填字段');
      return;
    }

    if (formState.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    setFormLoading(true);
    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formState.name.trim(),
          email: formState.email.trim(),
          message: formState.message.trim(),
        }).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_err) { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        setFormState({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch (_err) {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setFormLoading(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'luluca',
    description: 'luluca 智能创作平台，AI 赋能创意表达。',
    url: 'https://luluca.com/',
    email: aboutData.contact.email,
    telephone: aboutData.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '望京科技园A座18层',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    foundingDate: '2024',
    founder: {
      '@type': 'Person',
      name: '李明远',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-4">
                {aboutData.brandIntro.title}
              </h1>
              <p className="text-lg md:text-xl text-accent-600 font-heading mb-6">
                {aboutData.brandIntro.subtitle}
              </p>
              <p className="text-foreground-600 max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
                {aboutData.brandIntro.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="bg-primary-100/70 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-primary-800 mb-3">我们的愿景</h3>
                <p className="text-sm text-primary-700 leading-relaxed">{aboutData.brandIntro.vision}</p>
              </div>
              <div className="bg-accent-100/70 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-accent-800 mb-3">我们的使命</h3>
                <p className="text-sm text-accent-700 leading-relaxed">{aboutData.brandIntro.mission}</p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
                品牌价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutData.values.map((value) => (
                  <div key={value.title} className="bg-background-100 rounded-2xl p-6 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-4">
                      <i className={`${value.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              {aboutData.timeline.map((item, index) => (
                <div
                  key={item.year}
                  className={`relative flex items-start gap-6 mb-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="hidden md:flex flex-1"></div>
                  <div className="relative z-10 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-background-50"></div>
                  </div>
                  <div className="flex-1 bg-background-50 rounded-xl p-5">
                    <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-2">
                      {item.year}
                    </span>
                    <p className="text-sm text-foreground-700 leading-relaxed">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutData.teamMembers.map((member) => (
                <div key={member.name} className="bg-background-100 rounded-2xl p-6 text-center">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-24 h-24 mx-auto rounded-full object-cover mb-4"
                  />
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">{member.name}</h3>
                  <p className="text-xs text-primary-500 font-medium mb-3">{member.role}</p>
                  <p className="text-sm text-foreground-600 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              媒体 & 口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutData.testimonials.map((item) => (
                <div key={item.name} className="bg-background-50 rounded-2xl p-6 md:p-7">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.logo} text-lg`}></i>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div className="text-sm font-semibold text-foreground-900">{item.name}</div>
                  <div className="text-xs text-foreground-500">{item.role}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电话</h3>
                    <p className="text-sm text-foreground-600">{aboutData.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">地址</h3>
                    <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-600">{aboutData.contact.wechat}</p>
                  </div>
                </div>
              </div>

              <div>
                {formSubmitted ? (
                  <div className="bg-background-100 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">感谢你的留言！</h3>
                    <p className="text-sm text-foreground-600">我们会在1-2个工作日内回复你</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form="true"
                    onSubmit={handleFormSubmit}
                    className="bg-background-100 rounded-2xl p-6 md:p-8 space-y-5"
                  >
                    <h3 className="text-lg font-heading font-bold text-foreground-900 mb-1">给我们留言</h3>
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formState.name}
                        onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="你的名字"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={(e) => setFormState((s) => ({ ...s, email: e.target.value }))}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formState.message}
                        onChange={(e) => setFormState((s) => ({ ...s, message: e.target.value }))}
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        placeholder="请输入你的留言（最多500字）"
                        required
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">{formState.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    {formError && (
                      <p className="text-sm text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer"
                    >
                      {formLoading ? '发送中...' : '发送留言'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}